import {
  useApplicationsServiceApplicationsAccessCreate,
  useApplicationsServiceApplicationsApplicationsAccessList,
  useApplicationsServiceApplicationsApplicationsList,
  useOrganizationsServiceOrganizationsAccessRetrieve
} from '@organization/api/queries'
import { Application, ApplicationAccessList } from '@organization/api/requests'
import { useFormWizard } from '@organization/features/users/context'
import { useApplicationIcon } from '@organization/hooks'
import {
  AppIconsNames,
  applicationTypes
} from '@organization/layout/Applications'
import { paths } from '@organization/router'
import { useSessionContext } from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import {
  App,
  Avatar,
  Button,
  Flex,
  Select,
  Switch,
  Table,
  Typography
} from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useStyle } from './styles'

import type { ColumnsType } from 'antd/es/table'

const OrganizationCell = ({
  name,
  item
}: {
  name: string
  item: Application
}) => {
  const type = item?.instance_type?.type ?? 'cms'
  const applicationIcon = useApplicationIcon(type)
  const { icon: AppIcon } =
    applicationTypes[type] ?? applicationTypes[AppIconsNames.Default]
  const { styles } = useStyle({ applicationIcon })

  return (
    <Flex align="center" gap={8}>
      <Avatar size={56} icon={<AppIcon />} className={styles.applicationIcon} />
      <Typography.Title level={5}>{name}</Typography.Title>
    </Flex>
  )
}

export const PermissionsUserForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { userId } = useParams()
  const { user } = useSessionContext()
  const wizardContext = useFormWizard()
  const { notification } = App.useApp()
  const { styles } = useStyle()

  const [usersIdList, setUsersIdList] = useState<number[]>([])
  const [selectedAccessList, setSelectedAccessList] = useState<
    Partial<ApplicationAccessList[]>
  >([])

  const organizationId = Number(user?.organization?.id)

  const { data: userInfo } = useOrganizationsServiceOrganizationsAccessRetrieve(
    {
      id: Number(userId)
    },
    [Number(userId)],
    {
      enabled: !!userId
    }
  )

  const { data: applicationList, isLoading } =
    useApplicationsServiceApplicationsApplicationsList(
      { organization: organizationId },
      [organizationId],
      { enabled: !!organizationId }
    )

  const { data: applicationAccess } =
    useApplicationsServiceApplicationsApplicationsAccessList(
      {
        user: Number(userInfo?.user?.id)
      },
      [Number(userInfo?.user?.id)],
      {
        enabled: !!userInfo?.user?.id
      }
    )

  useEffect(() => {
    if (applicationAccess?.results) {
      setSelectedAccessList(applicationAccess.results)
    }
  }, [applicationAccess])

  useEffect(() => {
    const ids: number[] = []

    if (userId) ids.push(Number(userId))
    if (Array.isArray(wizardContext.data)) ids.push(...wizardContext.data)

    setUsersIdList([...new Set(ids)])
  }, [userId, wizardContext])

  const handleChange = (
    app: Application,
    role: string | null = null,
    isActive: boolean
  ) => {
    const appId = app.id

    const updated = isActive
      ? Array.from(
          new Map(
            [
              ...selectedAccessList,
              {
                application: app,
                role,
                user: userInfo
              }
            ].map((item) => [item?.application.id, item])
          ).values()
        )
      : selectedAccessList.filter((item) => item?.application.id !== appId)

    setSelectedAccessList(updated as ApplicationAccessList[])
  }

  const handleSubmit = () => {
    if (!organizationId) return

    const applications = selectedAccessList
      ?.filter((entry) => entry?.role !== null)
      ?.map((entry) => ({
        id: Number(entry?.application?.id),
        role: entry?.role
      }))

    const requestBody = {
      users: usersIdList,
      applications
    }

    mutate({ organizationId, requestBody })
  }

  const { mutate, isPending } = useApplicationsServiceApplicationsAccessCreate({
    onSuccess: () => {
      notification.success({
        message: t('users.permissions_changed')
      })
      navigate(paths.users.root)
    },
    onError: (error) => showApiErrors(error)
  })

  const columns: ColumnsType<Application> = [
    {
      key: 'organization',
      title: t('table.organization'),
      dataIndex: 'name',
      width: 1000,
      render: (name, item) => <OrganizationCell name={name} item={item} />
    },
    {
      key: 'active',
      title: t('table.active'),
      render: (item) => {
        const access = selectedAccessList.find(
          (a) => a?.application?.id === item?.id
        )
        const isActive = !!access
        const role = access?.role ?? null

        return (
          <Switch
            checked={isActive}
            onChange={(checked) => handleChange(item, role, checked)}
          />
        )
      }
    },
    {
      key: 'role',
      title: t('table.role'),
      width: 200,
      render: (item) => {
        const roles = item.instance_type?.roles ?? []
        const hasRole = Array.isArray(roles) && roles.length > 0

        const access = selectedAccessList.find(
          (a) => a?.application?.id === item?.id
        )
        const isActive = !!access
        const role = access?.role ?? undefined

        return (
          <Select
            className={styles.select}
            placeholder={
              hasRole
                ? t('form.placeholder.select_role')
                : t('form.placeholder.no_role')
            }
            disabled={!hasRole || !isActive}
            value={role}
            onChange={(newRole) => handleChange(item, newRole, isActive)}
          >
            {roles?.map((r: string) => (
              <Select.Option key={r} value={r}>
                {r}
              </Select.Option>
            ))}
          </Select>
        )
      }
    }
  ]

  return (
    <Table
      pagination={false}
      loading={isLoading}
      dataSource={applicationList?.results?.map((app) => ({
        ...app,
        key: app.id
      }))}
      columns={columns}
      footer={() => (
        <Flex justify="end" gap={8}>
          <Button onClick={() => navigate(paths.users.root)}>
            {t('button.cancel')}
          </Button>
          <Button type="primary" loading={isPending} onClick={handleSubmit}>
            {t('button.submit_adding')}
          </Button>
        </Flex>
      )}
    />
  )
}
